import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DescriptionIcon from '@mui/icons-material/Description'
import ImageIcon from '@mui/icons-material/Image'
import { Box, Button, Stack, Typography } from '@mui/material'
import { FC, useEffect, useRef, useState } from 'react'
import { ACCEPT, BRAND_CORAL, BRAND_CORAL_WASH, MAX_FILE_BYTES, MAX_FILE_MB } from '../constants'

const humanSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface Props {
    value: File | null
    onChange: (file: File | null) => void
    error?: string
}

const GstCertificateDropzone: FC<Props> = ({ value, onChange, error }) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [localError, setLocalError] = useState<string | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)

    useEffect(() => {
        if (!value || !value.type.startsWith('image/')) {
            setPreviewUrl(null)
            return undefined
        }
        const url = URL.createObjectURL(value)
        setPreviewUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [value])

    const accept = (file: File | undefined) => {
        if (!file) return
        if (file.size > MAX_FILE_BYTES) {
            setLocalError(
                `That file is ${humanSize(file.size)} — the limit is ${MAX_FILE_MB} MB. Please attach a smaller copy.`
            )
            return
        }
        setLocalError(null)
        onChange(file)
    }

    const shownError = localError ?? error

    if (value) {
        const isImage = value.type.startsWith('image/')
        return (
            <Box>
                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        backgroundColor: '#fff',
                    }}
                >
                    {previewUrl ? (
                        <Box
                            component="img"
                            src={previewUrl}
                            alt=""
                            sx={{
                                width: 48,
                                height: 48,
                                objectFit: 'cover',
                                borderRadius: 1,
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: 1,
                                backgroundColor: BRAND_CORAL_WASH,
                            }}
                        >
                            {isImage ? (
                                <ImageIcon sx={{ color: BRAND_CORAL }} />
                            ) : (
                                <DescriptionIcon sx={{ color: BRAND_CORAL }} />
                            )}
                        </Box>
                    )}

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {value.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {humanSize(value.size)}
                        </Typography>
                    </Box>

                    <Button
                        size="small"
                        onClick={() => inputRef.current?.click()}
                        sx={{ textTransform: 'none', color: BRAND_CORAL }}
                    >
                        Replace
                    </Button>
                    <Button
                        size="small"
                        onClick={() => {
                            onChange(null)
                            setLocalError(null)
                        }}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                        Remove
                    </Button>
                </Stack>

                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPT}
                    hidden
                    onChange={(event) => {
                        accept(event.target.files?.[0])
                        // Reset so picking the same file again still fires `change`.
                        event.target.value = ''
                    }}
                />
                {shownError && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {shownError}
                    </Typography>
                )}
            </Box>
        )
    }

    return (
        <Box>
            <Box
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        inputRef.current?.click()
                    }
                }}
                onDragOver={(event) => {
                    event.preventDefault()
                    setIsDragging(true)
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                    event.preventDefault()
                    setIsDragging(false)
                    accept(event.dataTransfer.files?.[0])
                }}
                sx={{
                    p: 4,
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: isDragging
                        ? BRAND_CORAL
                        : shownError
                          ? 'error.main'
                          : 'divider',
                    borderRadius: 2,
                    backgroundColor: isDragging ? BRAND_CORAL_WASH : '#fff',
                    cursor: 'pointer',
                    transition: 'border-color .15s, background-color .15s',
                    '&:hover': { borderColor: BRAND_CORAL },
                }}
            >
                <CloudUploadIcon
                    sx={{ fontSize: 40, color: isDragging ? BRAND_CORAL : 'text.disabled' }}
                />
                <Typography sx={{ mt: 1, fontWeight: 600 }}>
                    Drop your GST certificate here, or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    PDF, JPG or PNG · up to {MAX_FILE_MB} MB
                </Typography>
            </Box>

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                hidden
                onChange={(event) => {
                    accept(event.target.files?.[0])
                    event.target.value = ''
                }}
            />

            {shownError && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {shownError}
                </Typography>
            )}
        </Box>
    )
}

export default GstCertificateDropzone
